import { Modal, ModalContent, ModalOverlay } from "@chakra-ui/react";
import { useDeviceType } from "../../../hooks/use-device-type";
import { Icon } from "../ui";
import { BrandMark } from "../PublicHeader";
import type { AuthView } from "./AuthModalContext";
import { SignInForm } from "./SignInForm";
import { SignUpForm } from "./SignUpForm";
import { ForgotPasswordForm } from "./ForgotPasswordForm";

const labels: Record<AuthView, string> = {
  signin: "Sign in",
  signup: "Create account",
  forgot: "Reset password",
};

// The study center isn't supported on phones/tablets, so sign in/up show a notice there
const MobileNotice = ({ onClose }: { onClose: () => void }) => (
  <div className="flex flex-col items-center text-center gap-5 py-2">
    <div className="w-14 h-14 rounded-2xl bg-primary-fixed text-primary flex items-center justify-center">
      <Icon name="laptop_mac" className="text-3xl" />
    </div>
    <div className="flex flex-col gap-2">
      <h2 className="font-headline-md text-headline-md text-on-surface">
        Please use a laptop or desktop
      </h2>
      <p className="font-body-sm text-body-sm text-on-surface-variant">
        The study center access is not supported from mobile devices. Please
        sign in using your laptop or desktop computer.
      </p>
    </div>
    <button
      type="button"
      onClick={onClose}
      className="w-full h-12 rounded-xl bg-surface-container hover:bg-surface-container-high text-primary font-label-lg text-label-lg transition-colors"
    >
      Keep Browsing
    </button>
  </div>
);

type AuthModalProps = {
  view: AuthView | null;
  onSwitch: (view: AuthView) => void;
  onClose: () => void;
};

export const AuthModal = ({ view, onSwitch, onClose }: AuthModalProps) => {
  const isMobileDevice = useDeviceType();
  const showMobileNotice = isMobileDevice && view !== "forgot";

  return (
    <Modal
      isOpen={view !== null}
      onClose={onClose}
      isCentered
      motionPreset="slideInBottom"
      scrollBehavior="outside"
      returnFocusOnClose
    >
      <ModalOverlay bg="rgba(20, 27, 43, 0.45)" backdropFilter="blur(6px)" />
      <ModalContent
        aria-label={view ? labels[view] : undefined}
        bg="transparent"
        boxShadow="none"
        maxW="460px"
        w="calc(100% - 32px)"
        my={{ base: 4, sm: 16 }}
      >
        <div className="relative bg-surface-container-lowest rounded-3xl shadow-2xl overflow-hidden font-body-md text-body-md text-on-surface antialiased">
          {/* Accent glow, like the landing page hero */}
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
          <div className="relative p-6 sm:p-8 flex flex-col gap-6">
            <BrandMark asLink={false} />
            {view &&
              (showMobileNotice ? (
                <MobileNotice onClose={onClose} />
              ) : view === "signin" ? (
                <SignInForm onSwitch={onSwitch} />
              ) : view === "signup" ? (
                <SignUpForm onSwitch={onSwitch} onClose={onClose} />
              ) : (
                <ForgotPasswordForm onSwitch={onSwitch} />
              ))}
          </div>
          {/* Last in DOM order so focus lands on the first form field, not here */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center text-on-surface-variant hover:text-on-surface hover:bg-surface-container-low transition-colors"
          >
            <Icon name="close" className="text-[22px]" />
          </button>
        </div>
      </ModalContent>
    </Modal>
  );
};
